import React, { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Flex, Text } from 'rebass/styled-components';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import config from 'config';
import { Field, useFormikContext } from 'formik';
import { isEqual } from 'lodash';

import GroupInputSelectedCount from 'shared/components/atoms/GroupInputSelectedCount';
import ResponsiveDisplay from 'shared/components/atoms/ResponsiveDisplay';
import { Box, Image } from 'shared/components/display';
import EllipticalMenu from 'shared/components/EllipticalMenu/EllipticalMenu';
import ButtonGroupInput from 'shared/components/molecules/ButtonGroupInput/ButtonGroupInput';
import { DefinitionList, ListDetail } from 'shared/components/molecules/DefinitionList';
import { FilterForm } from 'shared/components/molecules/FilterForm';
import RangeInput from 'shared/components/molecules/RangeInput';
import { RangeTheme } from 'shared/components/organisms/RangeSlider';
import TextInput from 'shared/components/TextInput';

import axiosClient from 'shared/axiosClient';
import { API_TAG_ROUTES } from 'shared/config/routes/api/apiTagRoutes';
import { API_USER_ROUTES } from 'shared/config/routes/api/apiUserRoutes';
import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { OnSubmit } from 'shared/lib/formik';
import { useIntl } from 'shared/lib/intl';
import COLORS from 'shared/styles/colors';
import { Country, State } from 'shared/typings/address';
import { capitalizeWithoutConjunctions } from 'shared/utilities/stringUtility';
import { formatInitialModelValues } from 'shared/utilities/tagUtility';

import CreatorsCSVFormButton from '../../atoms/CreatorsCSVButton';

import {
  ageRangeLimits,
  heightRangeLimits,
  heightValueDisplayMap,
  initialSearchBarState,
  otherOption,
  recentlyActiveOptionsDefaultActive,
  superadminInitialSearchBarState,
  valueAccessorMap,
} from './config';
import styles, { classNames, FILTER_DROPDOWN_WIDTH, FILTER_WIDTH } from './styles';
import { FilterOption, GetAllUsersQuery, SearchBarFields, SearchBarState } from './types';
import { formatSearchQuery } from './utils';
import schema from './validation';

type Props = {
  onSubmit: (searchQuery: GetAllUsersQuery) => void;
};

const defaultValue = {
  recentlyActive: recentlyActiveOptionsDefaultActive,
};
const ellipticalMenuOffset = { mainAxis: 2 };

const BANNER_URL = config.CREATOR_EXPLORE_BANNER_URL;

/** Only the US and Canada have rows in the state table, so only they can offer a state list. */
const STATE_BEARING_COUNTRY_CODES = ['US', 'CA'];

/**
 * Keeps the State filter's options in step with the Country filter: it offers the states of the
 * selected countries, or of every state-bearing country when no country is selected. Selections
 * for a country that is deselected fall away with its states.
 */
const StateOptionsSync: React.FC<{ statesByCountry: Record<number, FilterOption<number>[]> }> = ({
  statesByCountry,
}) => {
  const { values, setFieldValue } = useFormikContext<SearchBarState>();
  const selectedCountryIds = useMemo(
    () => values.countries?.filter(({ selected }) => selected).map(({ value }) => value) ?? [],
    [values.countries],
  );

  useEffect(() => {
    const countryIds = selectedCountryIds.length ? selectedCountryIds : Object.keys(statesByCountry).map(Number);
    const selectedStates = new Set(values.states?.filter(({ selected }) => selected).map(({ value }) => value));
    const nextOptions = countryIds
      .flatMap((countryId) => statesByCountry[countryId] ?? [])
      .map((option) => ({ ...option, selected: selectedStates.has(option.value) }));

    if (!isEqual(nextOptions, values.states)) {
      setFieldValue(SearchBarFields.States, nextOptions);
    }
  }, [selectedCountryIds, statesByCountry, values.states, setFieldValue]);

  return null;
};

const CreatorSearch: React.FC<Props> = ({ onSubmit }) => {
  const [state, setState] = useState<{
    tags: Record<string, FilterOption<string>[]>;
    countries: FilterOption<number>[];
    statesByCountry: Record<number, FilterOption<number>[]>;
    loading: boolean;
  }>({
    tags: {},
    countries: [],
    statesByCountry: {},
    loading: true,
  });
  const { formatMessage } = useIntl();
  const { isSuperadmin } = useCurrentUser();

  const initialValues: SearchBarState = useMemo(() => {
    return isSuperadmin
      ? {
          ...superadminInitialSearchBarState,
          industry: state.tags.industry,
          niche: state.tags.niche,
          filmingBackdrop: state.tags['filming backdrop'],
          model: formatInitialModelValues(state.tags.model)?.models as FilterOption<string>[],
          ethnicity: [...(state.tags?.ethnicity || []), { ...otherOption }],
          language: [...(state.tags?.language || []), { ...otherOption }],
          countries: state.countries,
          states: [],
        }
      : {
          ...initialSearchBarState,
          industry: state.tags.industry,
          niche: state.tags.niche,
          filmingBackdrop: state.tags['filming backdrop'],
          model: formatInitialModelValues(state.tags.model)?.models as FilterOption<string>[],
          ethnicity: [...(state.tags?.ethnicity || []), { ...otherOption }],
          language: [...(state.tags?.language || []), { ...otherOption }],
          countries: state.countries,
          states: [],
        };
  }, [superadminInitialSearchBarState, initialSearchBarState, isSuperadmin, state.tags, state.countries]);

  const handleSubmit: OnSubmit<SearchBarState> = (state, { setSubmitting }) => {
    const searchQuery = formatSearchQuery(state, initialValues);
    onSubmit(searchQuery);
    setSubmitting(false);
  };

  useEffect(() => {
    const fetchTags = axiosClient.httpRequest<{ category: string; values: string[] }[]>({
      url: API_TAG_ROUTES.TAG_CATEGORIES,
      method: 'GET',
    });
    const fetchCountries = axiosClient.httpRequest<Country[]>({
      url: API_USER_ROUTES.COUNTRIES,
      method: 'GET',
    });

    Promise.all([fetchTags, fetchCountries])
      .then(async ([{ data: tagData }, { data: countryData }]) => {
        // The state table is only populated for the US and Canada (see the create_state_table
        // migration), so those are the only countries whose states are worth loading.
        const countriesWithStates = countryData.filter(
          ({ code }) => !!code && STATE_BEARING_COUNTRY_CODES.includes(code),
        );
        const stateResponses = await Promise.all(
          countriesWithStates.map((country) =>
            axiosClient
              .httpRequest<State[]>({
                url: API_USER_ROUTES.COUNTRY_STATES.replace(':countryId', String(country.id)),
                method: 'GET',
              })
              .then(({ data }) => [country.id, data] as const)
              .catch((error) => {
                console.error(error);
                return [country.id, [] as State[]] as const;
              }),
          ),
        );

        setState({
          tags: tagData.reduce((acc, val) => {
            acc[val.category] = val.values.map((val) => {
              return {
                value: val,
                label: capitalizeWithoutConjunctions(val),
                selected: false,
              };
            });
            return acc;
          }, {}),
          countries: countryData.map(({ id, name }) => ({ value: id, label: name, selected: false })),
          statesByCountry: stateResponses.reduce((acc, [countryId, states]) => {
            acc[countryId] = states.map(({ id, name }) => ({ value: id, label: name, selected: false }));
            return acc;
          }, {} as Record<number, FilterOption<number>[]>),
          loading: false,
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  if (state.loading) {
    return <Skeleton width="100%" height="50px" />;
  }

  return (
    <Box css={styles} className={classNames.base}>
      <ResponsiveDisplay display="desktop">
        <Image src={BANNER_URL} alt="Creator Explore Banner" className={classNames.banner} />
      </ResponsiveDisplay>
      <FilterForm<SearchBarState>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={schema}
        validateOnBlur={false}
        valueAccessorMap={valueAccessorMap}
      >
        <Flex className={classNames.search}>
          <>
            <Text className={classNames.search__icon}>
              <FontAwesomeIcon icon={faSearch} />
            </Text>
            <Field
              className={classNames.search__input}
              name={SearchBarFields.Search}
              placeholder={formatMessage({ id: 'CREATOR_SEARCH' })}
              component={TextInput}
              dataCy={'creator-search'}
            />
          </>

          {isSuperadmin && (
            <EllipticalMenu
              id="creator-search__superadmin-menu"
              className={classNames.search__menu}
              dataCy="creator-search__superadmin-menu"
              width="200px"
              height="100%"
              placement="bottom-end"
              color={COLORS.NEUTRAL700}
              offset={ellipticalMenuOffset}
            >
              <CreatorsCSVFormButton className={classNames.search__menu__btn} initialValues={initialValues} />
            </EllipticalMenu>
          )}
        </Flex>
        <Box className={classNames.filter}>
          {isSuperadmin && (
            <FilterForm.DropdownField<Pick<SearchBarState, 'vettingStatus'>>
              filterName="Vetting Status"
              fieldNames={[SearchBarFields.VettingStatus]}
              width={FILTER_WIDTH}
              dropdownWidth={FILTER_DROPDOWN_WIDTH}
            >
              <Box className="creator-search__button-group" css={styles}>
                <ButtonGroupInput
                  name={SearchBarFields.VettingStatus}
                  className={classNames.buttonGroup}
                  buttonAlignText="left"
                />
              </Box>
            </FilterForm.DropdownField>
          )}
          <FilterForm.DropdownField<Pick<SearchBarState, 'recentlyActive'>>
            filterName="Active"
            fieldNames={[SearchBarFields.RecentlyActive]}
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.RecentlyActive}
                className={classNames.buttonGroup}
                buttonAlignText="left"
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'industry'>>
            filterName="Industry"
            fieldNames={[SearchBarFields.Industry]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
            LeftFooter={<GroupInputSelectedCount name={SearchBarFields.Industry} />}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.Industry}
                className={classNames.buttonGroup}
                dataCy="creator-search__industry"
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'niche'>>
            filterName="Niche"
            fieldNames={[SearchBarFields.Niche]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
            LeftFooter={<GroupInputSelectedCount name={SearchBarFields.Niche} />}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.Niche}
                className={classNames.buttonGroup}
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'experience'>>
            filterName="Experience"
            fieldNames={[SearchBarFields.Experience]}
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.Experience}
                className={classNames.buttonGroup}
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'filmingBackdrop'>>
            filterName="Filming Backdrop"
            fieldNames={[SearchBarFields.FilmingBackdrop]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
            LeftFooter={<GroupInputSelectedCount name={SearchBarFields.FilmingBackdrop} />}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.FilmingBackdrop}
                className={classNames.buttonGroup}
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'model'>>
            filterName="Model"
            fieldNames={[SearchBarFields.Model]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
            LeftFooter={<GroupInputSelectedCount name={SearchBarFields.Model} />}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.Model}
                className={classNames.buttonGroup}
                dataCy="creator-search__model"
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'gender'>>
            filterName="Gender"
            fieldNames={[SearchBarFields.Gender]}
            maxHeight="300px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.Gender}
                className={classNames.buttonGroup}
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'ageRange'>>
            filterName="Age"
            fieldNames={[SearchBarFields.AgeRange]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
          >
            <Box className="creator-search__age" css={styles}>
              <DefinitionList>
                <ListDetail text="Filter by Range" hideLabel>
                  <Field
                    component={RangeInput}
                    name={SearchBarFields.AgeRange}
                    min={ageRangeLimits.min}
                    max={ageRangeLimits.max}
                    theme={RangeTheme.MINI}
                    hideTicks
                  />
                </ListDetail>
              </DefinitionList>
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'ethnicity'>>
            filterName="Ethnicity"
            fieldNames={[SearchBarFields.Ethnicity]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
            LeftFooter={<GroupInputSelectedCount name={SearchBarFields.Ethnicity} />}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.Ethnicity}
                className={classNames.buttonGroup}
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'height'>>
            filterName="Height"
            fieldNames={[SearchBarFields.Height]}
            maxHeight="300px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
          >
            <Field
              component={RangeInput}
              name={SearchBarFields.Height}
              min={heightRangeLimits.min}
              max={heightRangeLimits.max}
              theme={RangeTheme.MINI}
              valueDisplayMap={heightValueDisplayMap}
              hideTicks
            />
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'language'>>
            filterName="Language"
            fieldNames={[SearchBarFields.Language]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
            LeftFooter={<GroupInputSelectedCount name={SearchBarFields.Language} />}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.Language}
                className={classNames.buttonGroup}
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'countries'>>
            filterName="Country"
            fieldNames={[SearchBarFields.Countries]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
            LeftFooter={<GroupInputSelectedCount name={SearchBarFields.Countries} />}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.Countries}
                className={classNames.buttonGroup}
                dataCy="creator-search__countries"
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
          <FilterForm.DropdownField<Pick<SearchBarState, 'states'>>
            filterName="State"
            fieldNames={[SearchBarFields.States]}
            maxHeight="400px"
            width={FILTER_WIDTH}
            dropdownWidth={FILTER_DROPDOWN_WIDTH}
            LeftFooter={<GroupInputSelectedCount name={SearchBarFields.States} />}
          >
            <Box className="creator-search__button-group" css={styles}>
              <ButtonGroupInput
                name={SearchBarFields.States}
                className={classNames.buttonGroup}
                dataCy="creator-search__states"
                buttonAlignText="left"
                isMultiSelect
              />
            </Box>
          </FilterForm.DropdownField>
        </Box>
        <StateOptionsSync statesByCountry={state.statesByCountry} />
        <FilterForm.SetActiveDefaults defaultValue={defaultValue} />
      </FilterForm>
    </Box>
  );
};

export default CreatorSearch;
